import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Linkedin, Youtube } from 'lucide-react';
import gevoloLogo from '@/assets/gelovo-logo.png';

const SOCIAL_ICONS: Record<string, React.ReactNode> = {
  facebook_url: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
  ),
  instagram_url: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
  ),
  linkedin_url: <Linkedin className="w-4 h-4" />,
  youtube_url: <Youtube className="w-4 h-4" />,
  tiktok_url: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.73a8.19 8.19 0 004.76 1.52V6.8a4.84 4.84 0 01-1-.11z"/></svg>
  ),
};

const SOCIAL_LABELS: Record<string, string> = {
  facebook_url: 'Facebook',
  instagram_url: 'Instagram',
  linkedin_url: 'LinkedIn',
  youtube_url: 'YouTube',
  tiktok_url: 'TikTok',
};

const SOCIAL_KEYS = ['facebook_url', 'instagram_url', 'linkedin_url', 'youtube_url', 'tiktok_url'];

const Footer = () => {
  const { t, localePath } = useLanguage();
  const year = new Date().getFullYear();
  const [socialLinks, setSocialLinks] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchSocial = async () => {
      const { data } = await supabase
        .from('site_settings')
        .select('key, value')
        .in('key', SOCIAL_KEYS);
      const links: Record<string, string> = {};
      data?.forEach(d => {
        let val = d.value;
        if (typeof val === 'string') {
          try { val = JSON.parse(val); } catch {}
        }
        const url = typeof val === 'string' ? val : '';
        if (url.trim()) links[d.key] = url.trim();
      });
      setSocialLinks(links);
    };
    fetchSocial();
  }, []);

  const activeSocials = SOCIAL_KEYS.filter(k => socialLinks[k]);

  return (
    <footer className="bg-navy-medium border-t border-border/30 pt-12 md:pt-16 pb-8">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mb-10 md:mb-12 text-center md:text-start">
          {/* Brand */}
          <div className="flex flex-col items-center md:items-start">
            <img src="/images/logo.avif" alt={t('hero.title')} className="h-12 md:h-14 w-auto mb-3" />
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs mb-3">
              {t('hero.subtitle')}
            </p>
            <a href="mailto:amirassi@ono.ac.il" className="text-sm text-primary hover:underline">
              amirassi@ono.ac.il
            </a>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="text-sm font-semibold text-primary mb-4 uppercase tracking-wider">
              {t('nav.home')}
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: 'nav.about' as const, path: '/about' },
                { key: 'nav.cv' as const, path: '/cv' },
                { key: 'nav.lectures' as const, path: '/lectures' },
                { key: 'nav.media' as const, path: '/media' },
                { key: 'nav.contact' as const, path: '/contact' },
              ].map(({ key, path }) => (
                <Link
                  key={key}
                  to={localePath(path)}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  {t(key)}
                </Link>
              ))}
            </div>
          </div>

          {/* Social */}
          <div className="flex flex-col items-center md:items-start">
            <h4 className="text-sm font-semibold text-primary mb-4 uppercase tracking-wider">Social</h4>
            <div className="flex gap-3 justify-center md:justify-start">
              {activeSocials.map(key => (
                <a
                  key={key}
                  href={socialLinks[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={SOCIAL_LABELS[key]}
                  className="w-10 h-10 rounded-full bg-secondary/50 flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all"
                >
                  {SOCIAL_ICONS[key]}
                </a>
              ))}
              {activeSocials.length === 0 && (
                <span className="text-xs text-muted-foreground">—</span>
              )}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-border/30 pt-6 md:pt-8 flex flex-col items-center gap-4 md:flex-row md:justify-between">
          <p className="text-xs text-muted-foreground text-center">
            © {year} {t('hero.title')} — {t('footer.rights')}
          </p>
          <a
            href="https://gelovo.dev/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
          >
            <span>נבנה ע״י</span>
            <img src={gevoloLogo} alt="GELOVO" className="h-[1em] w-auto inline-block" />
            <span>GELOVO</span>
          </a>
          <div className="flex flex-wrap justify-center gap-4 md:gap-6">
            {[
              { key: 'footer.privacy' as const, path: '/privacy' },
              { key: 'footer.terms' as const, path: '/terms' },
              { key: 'footer.accessibility' as const, path: '/accessibility' },
            ].map(({ key, path }) => (
              <Link key={key} to={localePath(path)} className="text-xs text-muted-foreground hover:text-primary transition-colors">
                {t(key)}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
